import { ContactType } from '../../entities/contact/contact.type';
import { CrmSecurityRight } from './crm.security.module';
import { JobSectionVerb } from './job.section.rights';

export type CrmRecordKind = 'customers' | 'contractors' | 'suppliers' | 'employees' | 'teams' | 'companies';

const R = CrmSecurityRight;

/** The rights of each kind of CRM record. Companies are never added or deleted from the CRM, so they hold View and Edit only. */
export const CrmRecordRightIds: Record<CrmRecordKind, Partial<Record<JobSectionVerb, CrmSecurityRight>>> = {
    customers: { view: R.CustomersView, create: R.CustomersCreate, edit: R.CustomersEdit, delete: R.CustomersDelete },
    contractors: { view: R.ContractorsView, create: R.ContractorsCreate, edit: R.ContractorsEdit, delete: R.ContractorsDelete },
    suppliers: { view: R.SuppliersView, create: R.SuppliersCreate, edit: R.SuppliersEdit, delete: R.SuppliersDelete },
    employees: { view: R.EmployeesView, create: R.EmployeesCreate, edit: R.EmployeesEdit, delete: R.EmployeesDelete },
    teams: { view: R.TeamsView, create: R.TeamsCreate, edit: R.TeamsEdit, delete: R.TeamsDelete },
    companies: { view: R.CompaniesView, edit: R.CompaniesEdit },
};

/** Every right WBS-12231 added: the per-kind rights and View Financial Details. */
export const CRM_RECORD_RIGHTS: CrmSecurityRight[] = [
    ...Object.values(CrmRecordRightIds).flatMap(ids => Object.values(ids)),
    R.ViewFinancialDetails,
];

/** The View right of every row. */
export const CRM_RECORD_VIEW_RIGHTS: CrmSecurityRight[] = Object.values(CrmRecordRightIds).map(ids => ids.view);

/** Every Create, Edit and Delete right of every row. */
export const CRM_RECORD_WRITE_RIGHTS: CrmSecurityRight[] = Object.values(CrmRecordRightIds).flatMap(ids => [ids.create, ids.edit, ids.delete].filter(id => id !== undefined));

/** Replaced by the Sections / Tabs rows; kept on the page as deprecated so an admin still sees and can clear them, and because the Field App V2 and the Contractor Portal still read some of them. */
export const CRM_DEPRECATED_TAB_RIGHTS: CrmSecurityRight[] = [
    R.ManageCustomers, R.ManageServiceProviders, R.ManageSubcontractors, R.ManageSuppliers, R.ManageEmployees, R.ManageCompanies,
];

/** The actions each row offers, in the order of the Sections / Tabs columns. */
export function crmRecordVerbs(kind: CrmRecordKind): JobSectionVerb[] {

    const ids = CrmRecordRightIds[kind];

    return (['view', 'create', 'edit', 'delete'] as JobSectionVerb[]).filter(verb => ids[verb] !== undefined);
}

/** The row a CRM record belongs to; service providers are customers. Anything else (sites, system contacts, equipment) has no row and no per-kind right. */
export function crmRecordKindOf(type: ContactType | string | undefined): CrmRecordKind | null {

    switch (type) {
        case ContactType.Business:
        case ContactType.Individual:
        case ContactType.ServiceProvider:
            return 'customers';
        case ContactType.Subcontractor:
            return 'contractors';
        case ContactType.Supplier:
            return 'suppliers';
        case ContactType.Employee:
            return 'employees';
        case ContactType.Team:
            return 'teams';
        case ContactType.WorkBuddyCompany:
            return 'companies';
        default:
            return null;
    }
}

export function crmRecordRight(kind: CrmRecordKind, verb: JobSectionVerb): CrmSecurityRight | undefined {

    return CrmRecordRightIds[kind][verb];
}

function rowRights(kind: CrmRecordKind, view: boolean, writes: boolean): CrmSecurityRight[] {

    const ids = CrmRecordRightIds[kind];
    const writeIds = writes ? [ids.create, ids.edit, ids.delete].filter(id => id !== undefined) : [];

    return view || writeIds.length ? [ids.view, ...writeIds] : [];
}

/**
 * The per-kind rights a role held in effect before they existed (WBS-12232): Access saw every kind, each Manage right
 * added, edited and deleted its own kind (Manage Employees also teams), and the Manage rights that showed pay rates or
 * finance gave View Financial Details. Create, Edit and Delete always bring View. Applied once per role — system roles
 * in their class, custom roles by the tenant upgrade — never re-derived.
 */
export function deriveCrmRecordRights(rights: number[]): CrmSecurityRight[] {

    const has = (right: number) => rights.includes(right);
    const access = has(R.CanAccess);
    const manageCustomers = has(R.ManageCustomers) || has(R.ManageServiceProviders);
    const financial = manageCustomers || has(R.ManageSubcontractors) || has(R.ManageSuppliers) || has(R.ManageEmployees);

    return [
        ...rowRights('customers', access, manageCustomers),
        ...rowRights('contractors', access, has(R.ManageSubcontractors)),
        ...rowRights('suppliers', access, has(R.ManageSuppliers)),
        ...rowRights('employees', access, has(R.ManageEmployees)),
        ...rowRights('teams', access, has(R.ManageEmployees)),
        ...rowRights('companies', access, has(R.ManageCompanies)),
        ...(financial ? [R.ViewFinancialDetails] : []),
    ];
}

/** A system role's rights with the per-kind rights replaced by the derived ones, so a whole-module spread cannot grant more. */
export function withDerivedCrmRecordRights(rights: number[]): number[] {

    const existing = rights.filter(right => !CRM_RECORD_RIGHTS.includes(right));

    return [...new Set([...existing, ...deriveCrmRecordRights(existing)])];
}
